import React, { useState, useMemo, useEffect, useRef } from 'react';
import { JobComment, PackageTrackingDetails, RequestChangeResponse } from '../../../types';
import styled from 'styled-components';
import MaterialIcon from '../Shared/Icons/MaterialIcon';
import moment from 'moment';
import MessageSource from './MessageSourceEnum';
import toast from '../../../Scripts/App/displayToastMessage';
import { useInterval } from '../Shared/hooks';
import i18next from 'i18next';
import AutoResizeTextbox from '../Shared/TextInput/AutoResizeTextbox';

const MessageListContainer = styled.div`
    width: 100%;
    border: 1px solid #C8CFD2;
    background-color: white;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    // allow flex layout to set the height
    @media (max-width: 480px) {
        ${p => p.preview ? '' : 'flex: 1 1 auto;'}
        height: ${p => p.preview ? '210px' : '0'};
    }
`;

const MessageList = styled.div`
    width: 100%;    
    overflow-y: ${p => p.preview ? 'hidden' : 'auto'};
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 4px;

    @media (max-width: 480px) {
        height: 100%;
    }

    @media (min-width: 480px) {
        height: 320px; // lines up with map on right
    }
`;

const MessageRowLayout = styled.div`
    display: flex;
    flex-direction: row;
    ${p => p.isReply ? 'padding-left: 120px;' : ''}
    ${p => p.isReply ? 'justify-content: flex-end;' : ''}
    gap: 16px;
    width: 100%;
`;

const AvatarIcon = styled.div`
    background-color: #5C6164;
    width: 34px;
    height: 34px;
    border-radius: 17px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
`;

const AvatarText = styled.h4`
    color: white;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
`;

const MessageGroupDate = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const MessageDetailLayout = styled.div`
`;

const MessageGroupList = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
`;

const MessageDetails = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const SenderName = styled.span`
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
`;

const MessageTime = styled.span`
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
`;

const MessageBubble = styled.div`
    padding: 8px 12px 8px 12px;
    border-radius: ${p => p.isReply ? '8px 0px 8px 8px' : '0px 8px 8px 8px'};
    background-color: ${p => p.isReply ? '#E6F1FA' : '#F2F5F6'};
    white-space: pre-wrap;
`;

const ReplyLayout = styled.div`
    margin-top: 10px;
    display: flex;
    gap: 24px;
    position: relative;
    align-items: center;
`;

const ExpandCommentsButton = styled.button`
    border: none;
    background: transparent;
    text-align: center;
    height: 36px;
    flex: 0 0 auto;
`;

const ReplyButton = styled.button`
    border: none;
    background: transparent;
    text-align: center;
    height: 36px;

    @media (max-width: 480px) {
        position: absolute;
        right: 10px;
        top: calc(50% - 20px);
        z-index: 1000;
    }
`;

const ButtonIcon = styled(MaterialIcon)`
    color: #417F90;
    vertical-align: middle;
`;

interface DiscussionProps {
    packageDetails: PackageTrackingDetails;
    jobComments: JobComment[];
    setJobComments: (value: JobComment[]) => void;
    canReply: boolean;
    preview?: boolean;
    setMobileDiscussionVisible?: (value: boolean) => void;
};

interface MessageGroup {
    key: number;
    avatar: string;
    senderName: string;
    displayName: string;
    source: number | null;
    messageTime: string;
    displayTime: string;
    comments: JobComment[];
    displayDate?: string;
}

const MessageRow = ({ messageGroup }: { messageGroup: MessageGroup }) => {
    const isReply = messageGroup.source === MessageSource.Customer;
    return (<>
        {messageGroup.displayDate && <MessageGroupDate><span>{messageGroup.displayDate}</span></MessageGroupDate>}
        <MessageRowLayout isReply={isReply}>
        {messageGroup.avatar &&
            <AvatarIcon><AvatarText>{messageGroup.avatar}</AvatarText></AvatarIcon>
        }
        <MessageDetailLayout>
            <MessageDetails>
                <SenderName>{messageGroup.displayName}</SenderName>
                <MessageTime>{messageGroup.displayTime}</MessageTime>
            </MessageDetails>
            <MessageGroupList>
                {
                    messageGroup.comments.map(c =>
                        <MessageBubble key={c.JobCommentId} isReply={isReply}>
                        <span>{c.Comment}</span>
                    </MessageBubble>)
                }
            </MessageGroupList>
        </MessageDetailLayout>
        </MessageRowLayout>
    </>);
}

const ReplyInput = ({ customerName, addReply }: { customerName: string, addReply: (comment: JobComment) => void }) => {
    const [replyText, setReplyText] = useState('');

    const createReply = () => {
        const trimmedText = replyText.trim();
        if (trimmedText) {
            addReply({
                JobCommentId: 0,
                Created: moment().format(),
                Comment: trimmedText,
                SenderName: customerName,
                MessageSource: MessageSource.Customer
            });
        }
        setReplyText('');
    }

    const onReply = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            createReply();
        }
    }

    return (<ReplyLayout>
        <AutoResizeTextbox
            value={replyText}
            changeAction={e => setReplyText(e.currentTarget.value)}
            onKeyPress={onReply}
        />
        <ReplyButton onClick={createReply}>
            <ButtonIcon className="button-icon">send</ButtonIcon>
        </ReplyButton>
    </ReplyLayout>);
}

function messageGroupFromComment(comment: JobComment): MessageGroup {
    const splitName = comment.SenderName.split(' ').filter(s => s.length > 0);
    let avatar = '';
    if (comment.MessageSource !== MessageSource.Customer) {
        if (splitName.length === 0) {
            avatar = '?'
        } else if (splitName.length === 1) {
            avatar = splitName[0][0];
        } else {
            avatar = splitName[0][0] + splitName[splitName.length - 1][0];
        }
    }
    const displayName = comment.MessageSource !== MessageSource.Customer ? comment.SenderName : i18next.t('message_title_you')
    const displayTime = moment(comment.Created).format("HH:mm");
    return { key: comment.JobCommentId, avatar, senderName: comment.SenderName, displayName, source: comment.MessageSource, messageTime: comment.Created, displayTime, comments: [comment] }
}

function commentMatchesGroup(comment: JobComment, messageGroup: MessageGroup) {
    if (comment.SenderName === messageGroup.senderName && comment.MessageSource !== null && comment.MessageSource === messageGroup.source) {
        const messageTime = moment(comment.Created);
        const groupTime = moment(messageGroup.messageTime);
        const minutes = messageTime.diff(groupTime, 'minutes');
        return minutes < 5;
    }
    return false;
}

function getDisplayDate(messageTime: string): string {
    const m = moment(messageTime);
    const now = moment();
    const days = now.diff(m, 'days');
    if (days <= 0) {
        return i18next.t('today');
    } else if (days === 1) {
        return i18next.t('yesterday');
    } else if (days < 7) {
        return m.format('dddd');
    } else if (days < 365) {
        return m.format('dddd, MMMM Do');
    }
    return m.format('dddd, MMMM Do YYYY');
}

function buildMessageGroups(jobComments: JobComment[], preview?: boolean): MessageGroup[] {
    const messageGroups: MessageGroup[] = [];
    let previousGroup: MessageGroup | null = null;
    let currentGroup: MessageGroup | null = null;

    if (jobComments.length > 0) {
        const commentList = preview ? [jobComments[jobComments.length - 1]] : jobComments;

        for (const comment of commentList) {
            if (currentGroup && !commentMatchesGroup(comment, currentGroup)) {
                messageGroups.push(currentGroup);
                previousGroup = currentGroup;
                currentGroup = null;
            } else {
                if (currentGroup) {
                    currentGroup.comments.push(comment);
                }
            }

            if (currentGroup === null) {
                currentGroup = messageGroupFromComment(comment);
                if (previousGroup) {
                    const previousDate = moment(previousGroup.messageTime);
                    const currentDate = moment(currentGroup.messageTime);
                    if (currentDate.diff(previousDate, 'days') !== 0) {
                        currentGroup.displayDate = getDisplayDate(currentGroup.messageTime);
                    }
                } else {
                    currentGroup.displayDate = getDisplayDate(currentGroup.messageTime);
                }
            }
        }

        if (currentGroup) {
            messageGroups.push(currentGroup);
        }
    }
    return messageGroups;
}

function sendCommentRequest(jobId: number, comment: JobComment, onSuccess: () => void, onFailure: () => void) {
    const xhr = new XMLHttpRequest();
    const url = 'Package/AddJobComment';

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    const data = JSON.stringify({
        JobId: jobId,
        Created: comment.Created,
        Comment: comment.Comment,
        SenderName: comment.SenderName
    });

    xhr.onload = () => {
        const response = JSON.parse(xhr.responseText) as RequestChangeResponse;
        if (response.Success) {
            onSuccess();
        } else {
            if (response.Message) {
                toast(response.Message, response.MessageType ?? 'success');
            }
            onFailure();
        }
    }

    xhr.send(data);
}

const DiscussionComponent = ({ packageDetails, jobComments, setJobComments, canReply, preview, setMobileDiscussionVisible }: DiscussionProps) => {
    const initialMessageGroups = useMemo(() => buildMessageGroups(jobComments, preview), [jobComments, preview]);
    const [messageGroups, setMessageGroups] = useState(initialMessageGroups);

    const listRef = useRef<any>();

    const address = packageDetails.DeliveryOptions.IsDelivery ? packageDetails.DeliveryAddress : packageDetails.CollectionAddress;
    const name = address?.ContactName ?? address?.CustomerName ?? 'Customer';

    useEffect(() => {
        setMessageGroups(initialMessageGroups);
    }, [setMessageGroups, initialMessageGroups]);

    useEffect(() => {
        if (!preview) {
            listRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [messageGroups, preview]);

    const loadJobComments = () => {
        fetch(`Package/JobComments?jobId=${packageDetails.JobId}`)
            .then(res => res.json())
            .then((result) => {
                const currentLast = jobComments.slice(-1)[0];
                const newLast: JobComment | undefined = result.slice(-1)[0];
                if (newLast) {
                    if (!currentLast || currentLast.JobCommentId !== newLast.JobCommentId) {
                        setJobComments(result);
                    }
                }
            },
            (error) => {
                console.error(error);
            });
    }

    useInterval(loadJobComments, 30000);

    const addComment = (comment: JobComment) => {
        comment.JobCommentId = (jobComments.length * -1) -1;
        const originalMessageGroups = [...messageGroups];
        const newMessageGroups = [...messageGroups, messageGroupFromComment(comment)];
        setMessageGroups(newMessageGroups);

        function sendSucceeded() {
            setJobComments([...jobComments, comment]);
        }

        function sendFailed() {
            setMessageGroups(originalMessageGroups);
        }
        sendCommentRequest(packageDetails.JobId, comment, sendSucceeded, sendFailed);
    }

    return <>
        <MessageListContainer preview={preview}>
            <MessageList preview={preview}>
                {
                    messageGroups.map(m => <MessageRow messageGroup={m} key={m.key} />)
                }
                <div ref={listRef}></div>
            </MessageList>
            {(canReply && setMobileDiscussionVisible && preview) &&
                <ExpandCommentsButton onClick={() => setMobileDiscussionVisible(true)}>
                    <ButtonIcon>forum</ButtonIcon>
                </ExpandCommentsButton>}
        </MessageListContainer>
        {(canReply && !preview) && <ReplyInput customerName={name} addReply={addComment} />}
    </>
}

export default DiscussionComponent;